
import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { AuthHeader } from '@/components/auth/AuthHeader';
import { SignInTab } from '@/components/auth/SignInTab';
import { SignUpTab } from '@/components/auth/SignUpTab';
import { ResetPasswordTab } from '@/components/auth/ResetPasswordTab';
import { Card } from '@/components/ui/card';

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Utiliser le paramètre 'tab' de l'URL pour déterminer l'onglet actif
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === 'signup' ? 'signup' : 'signin');

  useEffect(() => {
    // Rediriger vers la page d'accueil au lieu du feed si déjà connecté
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    // Mettre à jour l'onglet actif si le paramètre d'URL change
    if (tabParam === 'signup') {
      setActiveTab('signup');
    } else if (tabParam === 'reset') {
      setActiveTab('reset');
    } else if (tabParam === 'signin') {
      setActiveTab('signin');
    }
  }, [tabParam]);

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const resetErrors = () => {
    setSuccess(null);
    setNetworkError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-100 dark:from-youtube-dark dark:to-youtube-dark-accent flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <AuthHeader />
        
        <Card className="border-0 shadow-xl bg-white/90 dark:bg-youtube-dark-accent/90 backdrop-blur-sm rounded-2xl overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 h-14 bg-gray-100/80 dark:bg-gray-800/30 rounded-t-xl border-b border-gray-200 dark:border-gray-700">
              <SignInTabTrigger active={activeTab} />
              <SignUpTabTrigger active={activeTab} />
              <ResetPasswordTabTrigger active={activeTab} />
            </TabsList>
            
            <SignInTab 
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              showPassword={showPassword}
              handlePasswordVisibility={handlePasswordVisibility}
              rememberMe={rememberMe}
              setRememberMe={setRememberMe}
              loading={loading}
              setLoading={setLoading}
              networkError={networkError}
              setNetworkError={setNetworkError}
              resetErrors={resetErrors}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  document.getElementById('signin-button')?.click();
                }
              }}
            />
            
            <SignUpTab 
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              showPassword={showPassword}
              handlePasswordVisibility={handlePasswordVisibility}
              loading={loading}
              setLoading={setLoading}
              success={success}
              setSuccess={setSuccess}
              resetErrors={resetErrors}
              setActiveTab={setActiveTab}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  document.getElementById('signup-button')?.click();
                }
              }}
            />
            
            <ResetPasswordTab 
              email={email}
              setEmail={setEmail}
              loading={loading}
              setLoading={setLoading}
              success={success}
              setSuccess={setSuccess}
              resetErrors={resetErrors}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  document.getElementById('reset-button')?.click();
                }
              }}
            />
          </Tabs>
        </Card>
        
        <div className="mt-6 text-center text-sm">
          <Link to="/" className="text-youtube-red hover:text-youtube-red-dark transition-colors duration-300 font-medium hover:underline flex items-center justify-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-home">
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

// Composants pour les déclencheurs d'onglets
const SignInTabTrigger = ({ active }: { active: string }) => (
  <TabsTrigger 
    value="signin" 
    className={`flex items-center gap-2 text-sm font-medium transition-all duration-300 ${active === 'signin' ? 'text-youtube-red dark:text-youtube-red' : 'text-gray-500 dark:text-gray-400'}`}
  >
    <svg className={`w-4 h-4 ${active === 'signin' ? 'text-youtube-red' : 'text-gray-400'} transition-colors duration-300`} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </svg>
    Connexion
  </TabsTrigger>
);

const SignUpTabTrigger = ({ active }: { active: string }) => (
  <TabsTrigger 
    value="signup" 
    className={`flex items-center gap-2 text-sm font-medium transition-all duration-300 ${active === 'signup' ? 'text-youtube-red dark:text-youtube-red' : 'text-gray-500 dark:text-gray-400'}`}
  >
    <svg className={`w-4 h-4 ${active === 'signup' ? 'text-youtube-red' : 'text-gray-400'} transition-colors duration-300`} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </svg>
    Inscription
  </TabsTrigger>
);

const ResetPasswordTabTrigger = ({ active }: { active: string }) => (
  <TabsTrigger 
    value="reset" 
    className={`flex items-center gap-2 text-sm font-medium transition-all duration-300 ${active === 'reset' ? 'text-youtube-red dark:text-youtube-red' : 'text-gray-500 dark:text-gray-400'}`}
  >
    <svg className={`w-4 h-4 ${active === 'reset' ? 'text-youtube-red' : 'text-gray-400'} transition-colors duration-300`} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="21" r="2" />
      <circle cx="20" cy="21" r="2" />
      <path d="M5.67 6H23l-1.68 8.39a2 2 0 0 1-2 1.61H8.75a2 2 0 0 1-2-1.74L5.23 2.74A2 2 0 0 0 3.25 1H1" />
    </svg>
    Récupération
  </TabsTrigger>
);

export default Auth;
